import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import type { IncidentPayload, IncidentUpdatePayload } from '@models/incidents';
import { useToast } from '@hooks/useToast';
import { getApiErrorMessage } from '@utils/apiError';

import {
  attachIncidentCustomers,
  createIncident,
  detachIncidentCustomer,
  updateIncident,
} from './api';

// An incident's name/status and its membership show up in three places: the incidents list, the
// incident detail (header + customers table) and the customer card's Incidents tab (fed by the
// dashboard-customers aggregate, cached under 'customers' / 'customer').
function invalidateIncidentViews(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['incidents'] });
  queryClient.invalidateQueries({ queryKey: ['incident'] });
  queryClient.invalidateQueries({ queryKey: ['incident-customers'] });
  queryClient.invalidateQueries({ queryKey: ['customers'] });
  queryClient.invalidateQueries({ queryKey: ['customer'] });
}

function failureMessage(action: string, error: Error): string {
  return `${action}: ${getApiErrorMessage(error) ?? error.message}`;
}

export function useCreateIncidentMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (payload: IncidentPayload) => createIncident(payload),
    onSuccess: () => {
      toast.success('Incident created');
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
    },
    onError: (error) => {
      toast.error(failureMessage('Failed to create incident', error));
    },
  });
}

export function useUpdateIncidentMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: IncidentUpdatePayload }) =>
      updateIncident(id, payload),
    onSuccess: () => {
      toast.success('Incident updated');
      invalidateIncidentViews(queryClient);
    },
    onError: (error) => {
      toast.error(failureMessage('Failed to update incident', error));
    },
  });
}

export function useAttachIncidentCustomersMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: attachIncidentCustomers,
    onSuccess: (result) => {
      const skipped =
        result.alreadyAttachedCount > 0 ? `, ${result.alreadyAttachedCount} already attached` : '';
      toast.success(`Attached ${result.attachedCount} customer(s)${skipped}`);
      invalidateIncidentViews(queryClient);
    },
    onError: (error) => {
      toast.error(failureMessage('Failed to attach customers', error));
    },
  });
}

export function useDetachIncidentCustomerMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: detachIncidentCustomer,
    onSuccess: () => {
      toast.success('Customer detached from incident');
      invalidateIncidentViews(queryClient);
    },
    onError: (error) => {
      toast.error(failureMessage('Failed to detach customer', error));
    },
  });
}
